// src/components/HistoricalPrecedents.jsx
import React, { useState } from 'react';
import { History, Globe, ExternalLink, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';

const HistoricalPrecedents = ({ 
  precedents = [], 
  defaultExpanded = false,
  embedded = false // New prop to indicate if this component is embedded in EnhancedQuoteCard
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  // If no precedents, don't render anything - this logic is correct
  if (precedents.length === 0) {
    return null;
  }
  
  // Content for the component
  const content = (
    <>
      {/* Introduction Section */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start">
          <div className="flex-shrink-0 mr-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertCircle size={20} className="text-amber-700" />
            </div>
          </div>
          <div>
            <h3 className="font-medium text-gray-900 mb-1">Where We've Seen This Before</h3>
            <p className="text-gray-700">
              This rhetoric is not new. Similar claims have been used in other democracies to undermine institutions and erode democratic norms. Examining these historical examples helps us understand the potential consequences.
            </p>
          </div>
        </div>
      </div>
      
      {/* Expanded Content - Historical cases */}
      {expanded && (
        <div className="divide-y divide-gray-200">
          {/* Individual Precedent Cases */}
          {precedents.map((precedent, index) => (
            <div key={index} className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                    <Globe size={16} className="text-amber-700" />
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">
                    {precedent.country} <span className="text-gray-500">({precedent.time_period})</span>
                  </h4>
                  
                  <div className="mt-2 space-y-2">
                    <p className="text-gray-700 text-sm">{precedent.description}</p>
                    
                    <div className="bg-red-50 border-l-4 border-red-400 px-3 py-2 mt-2">
                      <h5 className="text-xs font-semibold text-red-800 uppercase">Outcome</h5>
                      <p className="text-sm text-gray-700">{precedent.outcome}</p>
                    </div>
                    
                    {precedent.relevance_note && (
                      <div className="bg-blue-50 border-l-4 border-blue-400 px-3 py-2 mt-2">
                        <h5 className="text-xs font-semibold text-blue-800 uppercase">Why This Matters</h5>
                        <p className="text-sm text-gray-700">{precedent.relevance_note}</p>
                      </div>
                    )}
                    
                    {precedent.source_urls && precedent.source_urls.length > 0 && (
                      <div className="mt-3">
                        <h5 className="text-xs font-semibold text-gray-700">Sources</h5>
                        <div className="mt-1">
                          {precedent.source_urls.map((url, i) => (
                            <a 
                              key={i}
                              href={url} 
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline flex items-center mt-1"
                            >
                              <ExternalLink size={12} className="mr-1" />
                              {url.replace(/^https?:\/\/(www\.)?/i, '')}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  // Embedded version doesn't need header and expand button
  if (embedded) {
    return content;
  }
  
  // Standalone version includes header and footer
  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 mt-6">
      {/* Header */}
      <div className="bg-amber-700 p-4 text-white flex items-center justify-between">
        <div className="flex items-center">
          <History className="mr-2" size={20} />
          <h2 className="text-lg font-semibold">Historical Precedents</h2>
        </div>
        <button 
          onClick={() => setExpanded(!expanded)}
          className="flex items-center text-white bg-amber-800 hover:bg-amber-900 px-3 py-1 rounded-md text-sm"
        >
          {expanded ? (
            <>
              <ChevronUp size={16} className="mr-1" />
              Collapse
            </>
          ) : (
            <>
              <ChevronDown size={16} className="mr-1" />
              {precedents.length === 1 ? 'View Precedent' : `View ${precedents.length} Precedents`}
            </>
          )}
        </button>
      </div>
      
      {content}
      
      {/* Footer */}
      <div className="p-4 bg-gray-50 flex justify-between items-center text-sm">
        <span className="text-gray-600">
          Source: Freedom House, V-Dem Institute
        </span>
      </div>
    </div>
  );
};

export default HistoricalPrecedents;